import { memo } from 'react';
import { Avatar, Box, Button, Typography } from '@wishket/design-system';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { twMerge } from 'tailwind-merge';

import { CommentTextareaProps } from './CommentTextarea.type';

import { PATH } from '@/shared/constants';

export const CommentHeader = memo(
  ({ userId, imgUrl }: Pick<CommentTextareaProps, 'imgUrl' | 'userId'>) => {
    const pathname = usePathname();

    if (!userId) {
      return (
        <Typography variant="body1" className="text-w-gray-300">
          <Link
            href={`${PATH.AUTH_LOGIN}?next=${pathname}`}
            className="font-medium text-primary"
          >
            로그인
          </Link>
          하고 자유롭게 의견을 남겨주세요.
        </Typography>
      );
    }

    return (
      <Box className="flex items-center gap-2">
        <Avatar size="sm" imgUrl={imgUrl} />
        <Typography variant="body1" className="font-medium text-w-gray-600">
          {userId}
        </Typography>
      </Box>
    );
  }
);

export const CommentGuide = memo(
  ({
    userId,
    guideText,
  }: Pick<CommentTextareaProps, 'userId' | 'guideText'>) => {
    return (
      <Box
        className={twMerge(
          'h-auto min-h-10 w-full py-2',
          userId && 'bg-w-gray-10 px-4'
        )}
      >
        <Typography variant="body2" className="text-w-gray-500">
          {userId ? guideText : ''}
        </Typography>
      </Box>
    );
  }
);

export const CommentFooter = memo(
  ({
    userId,
    isEdit,
    onCancel,
    onSubmit,
    buttonName,
  }: Pick<
    CommentTextareaProps,
    'userId' | 'isEdit' | 'onCancel' | 'onSubmit' | 'buttonName'
  >) => {
    return (
      <Box className="flex h-[60px] w-full items-center justify-end gap-3 rounded-b-xl px-4 py-3">
        {isEdit && (
          <Button
            size="sm"
            variant="outlined"
            className="text-w-gray-600 hover:border-w-gray-200"
            onClick={onCancel}
          >
            취소
          </Button>
        )}
        <Button size="sm" variant="solid" disabled={!userId} onClick={onSubmit}>
          {buttonName}
        </Button>
      </Box>
    );
  }
);

CommentHeader.displayName = 'CommentHeader';
CommentGuide.displayName = 'CommentGuide';
CommentFooter.displayName = 'CommentFooter';
